import {
  Container,
  Row,
  Col,
  Card,
  ProgressBar,
  ListGroup,
} from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AssessmentScoreBar from '../components/assessment/AssessmentScoreBar';
import { useSupplierAssessments } from '../hooks/useSupplierAssessments';
import type { UserOrganisation } from '../types/types';

const complianceOverviewStyles = {
  hero: {
    border: '1px solid #dbe4ee',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
    background: '#ffffff',
  },
  heroHeader: {
    background:
      'linear-gradient(135deg, #ffffff 0%, #fafcff 60%, #f3f6f9 100%)',
    borderBottom: '1px solid #dbe4ee',
  },
  statCard: {
    border: '1px solid #dbe4ee',
    borderRadius: '20px',
    background: '#ffffff',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
  },
  panel: {
    border: '1px solid #dbe4ee',
    borderRadius: '22px',
    background: '#ffffff',
    boxShadow: '0 14px 32px rgba(15, 23, 42, 0.05)',
    overflow: 'hidden',
  },
  panelHeader: {
    background: '#f8fbfd',
    borderBottom: '1px solid #e2e8f0',
  },
} satisfies Record<string, React.CSSProperties>;

const MyComplianceOverview = () => {
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const { complianceOverview, overviewLoading, error } = useSupplierAssessments(
    selectedOrganisation?.organisation?.id,
  );

  const overview = complianceOverview;

  return (
    <Container fluid>
      <Card style={complianceOverviewStyles.hero} className="mb-4">
        <div style={complianceOverviewStyles.heroHeader} className="p-4 p-lg-5">
          <Row className="g-4 align-items-center">
            <Col lg={8}>
              <div
                className="text-uppercase fw-semibold mb-2"
                style={{
                  letterSpacing: '0.12em',
                  fontSize: '0.78rem',
                  color: '#64748b',
                }}
              >
                Compliance Data
              </div>
              <h3 className="mb-2">Compliance Overview</h3>
              <p className="text-muted mb-0">
                Review your completed assessments, evidence coverage, section
                performance, and high-level findings in one place.
              </p>
            </Col>
            <Col lg={4}>
              <div className="d-grid gap-2">
                <div
                  className="rounded-4 border p-3 bg-white"
                  style={{ borderColor: '#dbe4ee' }}
                >
                  <div className="text-muted small mb-1">Back to hub</div>
                  <Link
                    to="/my-compliance"
                    className="btn btn-outline-primary w-100"
                  >
                    Open My Compliance
                  </Link>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Card>

      {overviewLoading && <p>Loading Compliance...</p>}
      {error && <p className="text-danger">Error: {error}</p>}

      {overview && overview.assessment_count > 0 && (
        <>
          <Row className="g-3 mb-4">
            <Col sm={6} xl={3}>
              <Card style={complianceOverviewStyles.statCard} className="h-100">
                <Card.Body>
                  <div className="text-success fw-semibold small text-uppercase mb-2">
                    Average Compliance
                  </div>
                  <div className="d-flex align-items-end justify-content-between gap-3 mb-3">
                    <h2 className="mb-0">
                      {overview.average_compliance_percentage}%
                    </h2>
                    <i className="bi bi-shield-check fs-3 text-success"></i>
                  </div>
                  <ProgressBar
                    now={overview.average_compliance_percentage}
                    variant={
                      overview.average_compliance_percentage >= 75
                        ? 'success'
                        : overview.average_compliance_percentage >= 50
                          ? 'warning'
                          : 'danger'
                    }
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col sm={6} xl={3}>
              <Card style={complianceOverviewStyles.statCard} className="h-100">
                <Card.Body>
                  <div className="text-primary fw-semibold small text-uppercase mb-2">
                    Assessments Completed
                  </div>
                  <div className="d-flex align-items-end justify-content-between gap-3">
                    <h2 className="mb-0">{overview.assessment_count}</h2>
                    <i className="bi bi-clipboard-check fs-3 text-primary"></i>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col sm={6} xl={3}>
              <Card style={complianceOverviewStyles.statCard} className="h-100">
                <Card.Body>
                  <div className="text-info fw-semibold small text-uppercase mb-2">
                    File Completion
                  </div>
                  <div className="d-flex align-items-end justify-content-between gap-3 mb-3">
                    <h2 className="mb-0">
                      {overview.file_overview.completion_percentage}%
                    </h2>
                    <i className="bi bi-file-earmark-check fs-3 text-info"></i>
                  </div>
                  <ProgressBar
                    now={overview.file_overview.completion_percentage}
                    variant={
                      overview.file_overview.completion_percentage >= 90
                        ? 'success'
                        : 'warning'
                    }
                  />
                </Card.Body>
              </Card>
            </Col>

            <Col sm={6} xl={3}>
              <Card style={complianceOverviewStyles.statCard} className="h-100">
                <Card.Body>
                  <div className="text-warning fw-semibold small text-uppercase mb-2">
                    Files Missing
                  </div>
                  <div className="d-flex align-items-end justify-content-between gap-3">
                    <h2 className="mb-0">
                      {overview.file_overview.files_missing}
                    </h2>
                    <i className="bi bi-exclamation-triangle fs-3 text-warning"></i>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-4 mb-4">
            <Col xl={7}>
              <Card style={complianceOverviewStyles.panel}>
                <div
                  style={complianceOverviewStyles.panelHeader}
                  className="p-3 p-md-4"
                >
                  <h5 className="mb-1">File Overview</h5>
                  <div className="text-muted small">
                    Summary of file-based evidence across completed assessments.
                  </div>
                </div>
                <Card.Body className="p-3 p-md-4">
                  <ListGroup variant="flush">
                    <ListGroup.Item>
                      Total File Questions:{' '}
                      <strong>
                        {overview.file_overview.total_file_questions}
                      </strong>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      Files Provided:{' '}
                      <strong>{overview.file_overview.files_provided}</strong>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      Files Missing:{' '}
                      <strong>{overview.file_overview.files_missing}</strong>
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>

            <Col xl={5}>
              <Card style={complianceOverviewStyles.panel}>
                <div
                  style={complianceOverviewStyles.panelHeader}
                  className="p-3 p-md-4"
                >
                  <h5 className="mb-1">Missing Files</h5>
                  <div className="text-muted small">
                    Outstanding evidence still needed from assessments.
                  </div>
                </div>
                <Card.Body className="p-3 p-md-4">
                  {overview.file_overview.missing_files.length === 0 ? (
                    <p className="text-success mb-0">
                      All required files provided 🎉
                    </p>
                  ) : (
                    <ListGroup>
                      {overview.file_overview.missing_files.map((file, idx) => (
                        <ListGroup.Item key={idx}>
                          <div className="fw-bold">{file.section_title}</div>
                          <div className="text-muted">
                            {file.question_title}
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-4 mb-4">
            <Col xl={6}>
              <Card style={complianceOverviewStyles.panel}>
                <div
                  style={complianceOverviewStyles.panelHeader}
                  className="p-3 p-md-4"
                >
                  <h5 className="mb-1">Strong Sections</h5>
                  <div className="text-muted small">
                    Best-performing areas based on completed assessments.
                  </div>
                </div>
                <Card.Body className="p-3 p-md-4">
                  {overview.section_performance.strong_sections.map(
                    (section, idx) => (
                      <div key={idx} className="mb-3">
                        <div className="mb-1 fw-semibold">{section.title}</div>
                        <AssessmentScoreBar
                          value={section.average_percentage}
                        />
                      </div>
                    ),
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={6}>
              <Card style={complianceOverviewStyles.panel}>
                <div
                  style={complianceOverviewStyles.panelHeader}
                  className="p-3 p-md-4"
                >
                  <h5 className="mb-1">Weak Sections</h5>
                  <div className="text-muted small">
                    Areas that may need remediation or better evidence.
                  </div>
                </div>
                <Card.Body className="p-3 p-md-4">
                  {overview.section_performance.weak_sections.map(
                    (section, idx) => (
                      <div key={idx} className="mb-3">
                        <div className="mb-1 fw-semibold">{section.title}</div>
                        <AssessmentScoreBar
                          value={section.average_percentage}
                        />
                      </div>
                    ),
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card style={complianceOverviewStyles.panel}>
                <div
                  style={complianceOverviewStyles.panelHeader}
                  className="p-3 p-md-4"
                >
                  <h5 className="mb-1">High-Level Findings</h5>
                  <div className="text-muted small">
                    Key takeaways surfaced from your compliance overview.
                  </div>
                </div>
                <Card.Body className="p-3 p-md-4">
                  <ListGroup variant="flush">
                    {overview.high_level_findings.map((finding, idx) => (
                      <ListGroup.Item key={idx}>{finding}</ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default MyComplianceOverview;
